import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Activity, RefreshCw, XCircle, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { handleEdgeFunctionError, formatErrorForToast } from "@/lib/errorHandler";

interface Task {
  id: string;
  user_id: string;
  product_name: string;
  status: string;
  current_step: string | null;
  created_at: string;
  updated_at: string;
  error_message: string | null;
  profiles: { full_name: string | null } | null;
}

interface TaskStats {
  avgProcessingTime: number;
  failureRate: number;
  activeCount: number;
}

export function TaskMonitor() {
  const { toast } = useToast();
  const { session } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<TaskStats>({ avgProcessingTime: 0, failureRate: 0, activeCount: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadTasks();
    const interval = setInterval(loadTasks, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      // Get active tasks via admin-actions
      const { data: activeData, error: activeError } = await supabase.functions.invoke("admin-actions", {
        body: { action: "get_all_tasks", limit: 50, status: "active" },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (activeError || activeData?.error) {
        const errorInfo = handleEdgeFunctionError(activeError, activeData, 'admin');
        toast(formatErrorForToast(errorInfo));
        return;
      }
      setTasks(activeData?.tasks || []);

      // Get all tasks for stats
      const { data: allData, error: allError } = await supabase.functions.invoke("admin-actions", {
        body: { action: "get_all_tasks", limit: 1000 },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (allError || allData?.error) {
        const errorInfo = handleEdgeFunctionError(allError, allData, 'admin');
        toast(formatErrorForToast(errorInfo));
        return;
      }

      const allTasks = allData?.tasks || [];
      if (allTasks.length > 0) {
        const completed = allTasks.filter((t: any) => t.status === "completed");
        const failed = allTasks.filter((t: any) => t.status === "error");
        const active = allTasks.filter((t: any) => ["pending", "processing", "generating_image", "generating_video"].includes(t.status));

        const avgTime = completed.length > 0
          ? completed.reduce((sum: number, t: any) => {
              const start = new Date(t.created_at).getTime();
              const end = new Date(t.updated_at).getTime();
              return sum + (end - start);
            }, 0) / completed.length / 1000 / 60
          : 0;

        const failRate = allTasks.length > 0 ? (failed.length / allTasks.length) * 100 : 0;

        setStats({
          avgProcessingTime: avgTime,
          failureRate: failRate,
          activeCount: active.length,
        });
      }
    } catch (error) {
      console.error("Error loading tasks:", error);
      const errorInfo = handleEdgeFunctionError(error, null, 'admin');
      toast(formatErrorForToast(errorInfo));
    } finally {
      setLoading(false);
    }
  };

  const cancelTask = async () => {
    if (!selectedTask || !cancelReason.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, informe o motivo do cancelamento.",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "cancel_task",
          taskId: selectedTask.id,
          reason: cancelReason,
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error || data?.error) {
        const errorInfo = handleEdgeFunctionError(error, data, 'admin');
        toast(formatErrorForToast(errorInfo));
        return;
      }

      toast({
        title: "Task cancelada",
        description: "A task foi cancelada com sucesso.",
      });

      setSelectedTask(null);
      setCancelReason("");
      loadTasks();
    } catch (error: any) {
      console.error("Error canceling task:", error);
      const errorInfo = handleEdgeFunctionError(error, null, 'admin');
      toast(formatErrorForToast(errorInfo));
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "error":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "processing":
      case "generating_image":
      case "generating_video":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
    }
  };

  const getTimeElapsed = (createdAt: string) => {
    const now = new Date().getTime();
    const created = new Date(createdAt).getTime();
    const elapsed = (now - created) / 1000 / 60; // minutes
    
    if (elapsed < 60) {
      return `${Math.floor(elapsed)}m`;
    }
    return `${Math.floor(elapsed / 60)}h ${Math.floor(elapsed % 60)}m`;
  };

  return (
    <>
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Tasks Ativas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.activeCount}</p>
              <p className="text-xs text-muted-foreground mt-1">Em processamento</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Tempo Médio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.avgProcessingTime.toFixed(1)}m</p>
              <p className="text-xs text-muted-foreground mt-1">Processamento</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Taxa de Falha
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.failureRate.toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground mt-1">Total de erros</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Monitor de Tasks em Tempo Real
                </CardTitle>
                <CardDescription>{tasks.length} tasks ativas</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={loadTasks}>
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg">
              <div className="max-h-[500px] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium">Task</th>
                      <th className="text-left p-3 text-sm font-medium">Usuário</th>
                      <th className="text-left p-3 text-sm font-medium">Status</th>
                      <th className="text-left p-3 text-sm font-medium">Step</th>
                      <th className="text-left p-3 text-sm font-medium">Tempo</th>
                      <th className="text-left p-3 text-sm font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="text-center p-8 text-muted-foreground">
                          Carregando tasks...
                        </td>
                      </tr>
                    ) : tasks.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center p-8 text-muted-foreground">
                          Nenhuma task ativa no momento
                        </td>
                      </tr>
                    ) : (
                      tasks.map((task) => (
                        <tr key={task.id} className="border-t hover:bg-muted/50">
                          <td className="p-3 text-sm max-w-[200px] truncate">
                            {task.product_name}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {task.profiles?.full_name || "Unknown"}
                          </td>
                          <td className="p-3">
                            <span className={`text-xs px-2 py-1 rounded font-medium ${getStatusColor(task.status)}`}>
                              {task.status}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {task.current_step || "-"}
                          </td>
                          <td className="p-3 text-sm font-mono">
                            {getTimeElapsed(task.created_at)}
                          </td>
                          <td className="p-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedTask(task)}
                            >
                              Cancelar
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Task</DialogTitle>
            <DialogDescription>
              Esta ação marcará a task como erro e impedirá seu processamento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Task</Label>
              <p className="text-sm text-muted-foreground">{selectedTask?.product_name}</p>
            </div>
            <div>
              <Label>Status Atual</Label>
              <p className="text-sm">{selectedTask?.status}</p>
            </div>
            <div>
              <Label htmlFor="cancel-reason">Motivo do Cancelamento *</Label>
              <Textarea
                id="cancel-reason"
                placeholder="Descreva o motivo do cancelamento..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedTask(null)}>
              Voltar
            </Button>
            <Button
              variant="destructive"
              onClick={cancelTask}
              disabled={processing || !cancelReason.trim()}
            >
              {processing ? "Cancelando..." : "Confirmar Cancelamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
